import { useState, useEffect } from 'react';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { usePreference } from '../../lib/hooks/use-preference';
import { AnomalyActionHero } from '../../components/AnomalyActionHero';
import { useLiveChannel } from '../../lib/hooks/use-live-channel';
import { useQuery } from '@tanstack/react-query';
import { useUserName } from '../../lib/hooks/use-user-name';
import { useMe } from '../../lib/hooks/use-me';
import { useWidgetLayout } from '../../lib/config/widget-layout';
import { CustomizeBar, WidgetCanvas } from '../../components/CustomizeBar';
import { useKpiSelection } from '../../lib/config/kpi-config';
import { OnboardingChecklist } from '../../components/OnboardingChecklist';
import { api } from '../../lib/api';
import { emit } from '../../lib/analytics';
import { getRevenueForecast } from '../../lib/revenue-forecast';
import { getMeetingPrep } from '../../lib/meeting-prep';
import { getFollowUpQueue } from '../../lib/follow-up';
import { getDealRisk } from '../../lib/deal-risk';
import { getEscalationActions } from '../../lib/escalation';
import { getWeeklyDigest } from '../../lib/weekly-digest';
import { getMorningBriefing } from '../../lib/morning-briefing';
import { getTrustScores } from '../../lib/trust-scores';
import { getTeamForecast } from '../../lib/team-forecast';
import { ForwardLookingPanel } from '../../components/charts/ForwardLookingPanel';
import { WeeklyRecapCard } from '../../components/cards/WeeklyRecapCard';
import { ActionFeedCard } from '../../components/feeds/ActionFeedCard';

import { MorningBriefingCard } from './command-center/MorningBriefingCard';
import { KpiRibbon } from './command-center/KpiRibbon';
import {
  EscalationsWidget,
  FollowUpsWidget,
  ScheduleWidget,
  WeeklyPulseWidget,
  DealRiskWidget,
  ClientHealthWidget,
  CashRunwayWidget,
  RecentWinsWidget,
  TopDraftsWidget,
  AnomalyAlertsWidget,
  BusinessPulseWidget,
  TeamSnapshotWidget,
  TrustAlertsWidget,
  ConcentrationWidget,
} from './command-center/widgets';

export function CommandCenter() {
  const { firstName } = useUserName();
  // Design-partner workspace: no nerve center (Business Pulse). The API 403s it too (hideFromPartners).
  const partner = useMe().features?.partner === true;

  useEffect(() => { emit('session_start'); }, []);

  // Stagger staleTime values to prevent thundering-herd
  const revenue = useQuery({ queryKey: ['revenue-forecast'], queryFn: getRevenueForecast, staleTime: 5 * 60_000, refetchOnWindowFocus: false });
  const meetings = useQuery({ queryKey: ['meeting-prep'], queryFn: getMeetingPrep, staleTime: 3 * 60_000, refetchOnWindowFocus: false });
  const followUps = useQuery({ queryKey: ['follow-ups'], queryFn: getFollowUpQueue, staleTime: 4 * 60_000, refetchOnWindowFocus: false });
  const dealRisk = useQuery({ queryKey: ['deal-risk'], queryFn: getDealRisk, staleTime: 6 * 60_000, refetchOnWindowFocus: false });
  const escalations = useQuery({ queryKey: ['escalations'], queryFn: getEscalationActions, staleTime: 5 * 60_000 });

  // Live updates via SSE
  const liveEscalations = useLiveChannel('escalations', ['escalations']);
  const liveActions = useLiveChannel('actions', ['escalations']);
  const liveStatus = liveEscalations.status === 'open' || liveActions.status === 'open' ? 'open' : liveEscalations.status;

  const digest = useQuery({ queryKey: ['weekly-digest'], queryFn: getWeeklyDigest, staleTime: 10 * 60_000 });

  // ── Sales intelligence feeds ─────────────────────────────────────
  const nerveCenter = useQuery<{
    overall_health: number;
    trend: 'improving' | 'stable' | 'declining' | 'critical';
    dimensions: { revenue_health: number; client_health: number; pipeline_health: number; operational_health: number; relationship_health: number; cash_health: number };
    priority_signals: Array<{ rank: number; source_model: string; entity: string; revenue_impact_cents: number; urgency: string; recommended_action: string }>;
    forward_look: { revenue_forecast_7d_cents: number; revenue_forecast_30d_cents: number; churn_risk_count: number; expansion_opportunity_count: number; deals_expected_to_close: number };
  }>({
    queryKey: ['nerve-center'],
    queryFn: () => api('/v1/nerve-center'),
    staleTime: 5 * 60_000,
    retry: 0,
    enabled: !partner,
  });

  const trustAlerts = useQuery({ queryKey: ['trust-scores'], queryFn: getTrustScores, staleTime: 5 * 60_000, retry: 0 });
  const teamSnapshot = useQuery({ queryKey: ['team-forecast'], queryFn: getTeamForecast, staleTime: 5 * 60_000, retry: 0 });

  const { selectedKpis, selectedIds, setSelection } = useKpiSelection();
  const [showKpiPicker, setShowKpiPicker] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const DEFAULT_LAYOUT = [
    { id: 'business-pulse',  column: 'right' as const },
    { id: 'action-feed',     column: 'left'  as const },
    { id: 'team-snapshot',   column: 'right' as const },
    { id: 'schedule',        column: 'right' as const },
    { id: 'trust-alerts',    column: 'left'  as const },
    { id: 'forward-look',    column: 'right' as const },
    { id: 'weekly-recap',    column: 'left'  as const },
    // Every widget visible by default: nothing is hidden from a design partner (Nick, 2026-10-26).
    { id: 'weekly-pulse',    column: 'left'  as const },
    { id: 'deal-risk',       column: 'right' as const },
    { id: 'escalations',     column: 'left'  as const },
    { id: 'followups',       column: 'right' as const },
    { id: 'client-health',   column: 'left'  as const },
    { id: 'cash-runway',     column: 'right' as const },
    { id: 'recent-wins',     column: 'left'  as const },
    { id: 'top-drafts',      column: 'right' as const },
    { id: 'anomaly-alerts',  column: 'left'  as const },
    { id: 'concentration',   column: 'right' as const },
  ];
  const layout = useWidgetLayout('command-center-v3', DEFAULT_LAYOUT);
  const widgetLabels: Record<string, string> = {
    'business-pulse': 'Business Pulse',
    'action-feed':    'Action Feed',
    'team-snapshot':  'Team Snapshot',
    'trust-alerts':   'Trust Alerts',
    'schedule':       "Today's Schedule",
    'forward-look':   'Forward Look',
    'weekly-recap':   'Weekly Recap',
    'weekly-pulse':   'Weekly Pulse',
    'deal-risk':      'Deal Risk',
    'escalations':    'Needs Attention',
    'followups':      'Follow-Up Queue',
    'client-health':  'Client Health',
    'cash-runway':    'Cash Runway',
    'recent-wins':    'Recent Wins',
    'top-drafts':     'Drafts Waiting',
    'anomaly-alerts': 'Anomaly Alerts',
    'concentration':  'Concentration Risk',
  };

  const briefing = useQuery({ queryKey: ['morning-briefing'], queryFn: getMorningBriefing, staleTime: 5 * 60_000 });
  const [briefingExpanded, setBriefingExpanded] = usePreference<boolean>('briefing_expanded', true);

  function renderWidget(id: string) {
    switch (id) {
      case 'business-pulse': return partner ? null : <BusinessPulseWidget nerveCenter={nerveCenter} />;
      case 'action-feed':    return <ActionFeedCard escalations={escalations.data} followUps={followUps.data} isLoading={escalations.isLoading || followUps.isLoading} isError={escalations.isError || followUps.isError} onRetry={() => { escalations.refetch(); followUps.refetch(); }} />;
      case 'team-snapshot':  return <TeamSnapshotWidget teamSnapshot={teamSnapshot} />;
      case 'trust-alerts':   return <TrustAlertsWidget trustAlerts={trustAlerts} />;
      case 'forward-look':   return <ForwardLookingPanel />;
      case 'schedule':       return <ScheduleWidget meetings={meetings} />;
      case 'weekly-recap':   return <WeeklyRecapCard />;
      // Legacy widgets — available via Customize
      case 'weekly-pulse':   return <WeeklyPulseWidget digest={digest} />;
      case 'deal-risk':      return <DealRiskWidget dealRisk={dealRisk} />;
      case 'escalations':    return <EscalationsWidget escalations={escalations} />;
      case 'followups':      return <FollowUpsWidget followUps={followUps} />;
      case 'client-health':  return <ClientHealthWidget />;
      case 'cash-runway':    return <CashRunwayWidget />;
      case 'recent-wins':    return <RecentWinsWidget />;
      case 'top-drafts':     return <TopDraftsWidget />;
      case 'anomaly-alerts': return <AnomalyAlertsWidget escalations={escalations} />;
      case 'concentration':  return <ConcentrationWidget />;
      default:               return null;
    }
  }

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-6 space-y-6 animate-fade-in">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Command Center' }]} />
      <h1 className="text-xl font-semibold text-ink">Command Center</h1>
      <OnboardingChecklist />

      {/* Gasp moment — AI-detected anomaly + proposed action */}
      <AnomalyActionHero />

      {/* Morning Briefing */}
      <MorningBriefingCard
        briefing={briefing}
        briefingExpanded={briefingExpanded}
        setBriefingExpanded={setBriefingExpanded}
        revenue={revenue}
        meetings={meetings}
        digest={digest}
        escalations={escalations}
        followUps={followUps}
        dealRisk={dealRisk}
        liveStatus={liveStatus}
        firstName={firstName}
      />

      {/* KPI Ribbon */}
      <KpiRibbon
        selectedKpis={selectedKpis}
        selectedIds={selectedIds}
        setSelection={setSelection}
        showKpiPicker={showKpiPicker}
        setShowKpiPicker={setShowKpiPicker}
        revenue={revenue}
        dealRisk={dealRisk}
        followUps={followUps}
        escalations={escalations}
      />

      {/* Customize bar */}
      <CustomizeBar
        layout={layout}
        widgetLabels={widgetLabels}
        editMode={editMode}
        onToggleEdit={setEditMode}
      />

      {/* Widget layout */}
      <WidgetCanvas
        layout={layout}
        widgetLabels={widgetLabels}
        editMode={editMode}
        renderWidget={renderWidget}
      />
    </div>
  );
}
